// apps/web/src/stores/auth.ts
// In-memory only — no persistence; accessToken never touches localStorage.
// See useAuthBootstrap for the two-step hydration that flips `hydrating` to
// false on mount.
import { create } from 'zustand';
import type { PermissionCode, User } from '@magimp/shared';

export type PermissionsBundle = readonly PermissionCode[] | 'ALL';

interface AuthState {
  accessToken: string | null;
  user: User | null;
  permissions: PermissionsBundle;
  // INVARIANT (#440 Phase 3): the authoritative shift flag on the wire
  //   is `MeResponse.isOnShift` at the top level (#426). `UserSchema`
  //   no longer carries the field, so this slot is the single in-memory
  //   source of truth for the toggle UI. `null` = no session yet.
  isOnShift: boolean | null;
  // INVARIANT (#439 race-guard): bumps on every `setUserShift` call.
  //   Async reconcilers (e.g. the post-login /auth/me round-trip in
  //   `LoginForm`) capture this value before their request and apply
  //   their write only if it is still equal afterwards — protecting
  //   a same-user shift toggle that happened mid-flight from being
  //   clobbered by a stale server snapshot. MONOTONIC across sessions:
  //   neither `setSession` nor `clear` resets it. Resetting on
  //   `setSession` would let a same-user setSession path (e.g.
  //   `ProfilePage` self-edit) erase a prior `setUserShift` increment,
  //   making a stale /auth/me look fresh.
  shiftWriteCount: number;
  // INVARIANT (#443 race-guard): bumps on `clear()` only (NOT
  //   `setSession`). Pairs with `setUserShift`'s capture-on-dispatch
  //   to bound stale writes that span a session boundary — e.g. a
  //   PATCH /me/shift response from session N landing after the user
  //   logged out and back in as the SAME user (session N+1). The
  //   cross-session boundary is always logout → clear → setSession,
  //   so bumping in `clear()` is sufficient; bumping in `setSession`
  //   too would defeat in-flight toggles when `ProfilePage`/
  //   `useAuthBootstrap` perform a same-session refresh through the
  //   same setter.
  sessionRevision: number;
  hydrating: boolean;
  // WHY: distinguishes "throttled-bootstrap, retry pending" from "no session".
  // Without this, exhausting the inner /auth/refresh 429 retry budget falls
  // through to clear() and ProtectedRoute redirects a still-valid cookie
  // holder to /login. ProtectedRoute treats this as still-hydrating.
  bootstrapThrottled: boolean;
  setSession: (t: string, u: User, p: PermissionsBundle, isOnShift: boolean) => void;
  setAccessToken: (t: string) => void;
  // INVARIANT (#417): targeted updater for the self-toggle endpoint
  //   `PATCH /api/users/me/shift`. The response carries the post-toggle
  //   `isOnShift` flag; we patch it onto the dedicated session slot
  //   without forcing a `/auth/me` round-trip (which is imperative in
  //   `useAuthBootstrap`, not React Query — invalidating a key would
  //   not refetch).
  // INVARIANT (#439, #443 race-guard): callers pass BOTH the `userId`
  //   AND the `sessionRevision` they captured at request-dispatch
  //   time. We no-op on either mismatch:
  //     - userId mismatch — late response landing after a re-login as
  //       a DIFFERENT user (already covered before #443).
  //     - sessionRevision mismatch — late response landing after a
  //       logout + re-login as the SAME user (covered by #443; the
  //       userId check passes for same-user re-login, only the
  //       revision distinguishes the two sessions).
  //   The monotonic `shiftWriteCount` only bumps on a real apply.
  setUserShift: (userId: string, sessionRevision: number, isOnShift: boolean) => void;
  clear: () => void;
  setHydrating: (h: boolean) => void;
  setBootstrapThrottled: (v: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  permissions: [],
  isOnShift: null,
  shiftWriteCount: 0,
  sessionRevision: 0,
  hydrating: true,
  bootstrapThrottled: false,
  // INVARIANT: setSession + clear MUST reset bootstrapThrottled. Hook-level
  // resets are fragile under refactor; store-level resets keep the flag in
  // sync with terminal session transitions.
  setSession: (accessToken, user, permissions, isOnShift) =>
    set({
      accessToken,
      user,
      permissions,
      isOnShift,
      hydrating: false,
      bootstrapThrottled: false,
    }),
  setAccessToken: (accessToken) => set({ accessToken }),
  setUserShift: (userId, sessionRevision, isOnShift) =>
    set((s) => {
      // No-op on cross-user OR cross-session mismatch — see interface
      // comment above for the two race windows we close here.
      if (s.user?.id !== userId) return s;
      if (s.sessionRevision !== sessionRevision) return s;
      return {
        isOnShift,
        shiftWriteCount: s.shiftWriteCount + 1,
      };
    }),
  clear: () =>
    set((s) => ({
      accessToken: null,
      user: null,
      permissions: [],
      isOnShift: null,
      hydrating: false,
      bootstrapThrottled: false,
      sessionRevision: s.sessionRevision + 1,
    })),
  setHydrating: (hydrating) => set({ hydrating }),
  setBootstrapThrottled: (bootstrapThrottled) => set({ bootstrapThrottled }),
}));

export function hasPermission(perms: PermissionsBundle, code: PermissionCode): boolean {
  if (perms === 'ALL') return true;
  return perms.includes(code);
}
